// Before / after compare: the "after" image fills the box and the "before" image is revealed up to a draggable divider. Drag
// anywhere, or focus the handle and use ←/→ (Home/End jump to the ends). Fills its parent — the caller sizes and rounds the box.
// `afterStyle` styles the after image (e.g. a CSS filter for on-screen edits); `position` is both images' object-position.
import { useCallback, useRef, useState } from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { cn } from '@/lib/utils';

export default function CompareSlider({ before, after, beforeLabel, afterLabel, fit = 'cover', position, initial = 50, className, afterStyle }) {
    const [pos, setPos] = useState(initial); // divider position, % from the left
    const boxRef = useRef(null);
    const draggingRef = useRef(false);

    const moveTo = useCallback((clientX) => {
        const r = boxRef.current?.getBoundingClientRect();
        if (!r?.width) return;
        setPos(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
    }, []);

    const onPointerDown = (e) => {
        draggingRef.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        moveTo(e.clientX);
    };
    const onPointerMove = (e) => { if (draggingRef.current) moveTo(e.clientX); };
    const onPointerUp = () => { draggingRef.current = false; };
    const onKeyDown = (e) => {
        const step = e.shiftKey ? 10 : 2;
        if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - step));
        else if (e.key === 'ArrowRight') setPos((p) => Math.min(100, p + step));
        else if (e.key === 'Home') setPos(0);
        else if (e.key === 'End') setPos(100);
        else return;
        e.preventDefault();
    };

    const imgCls = cn('pointer-events-none absolute inset-0 h-full w-full select-none', fit === 'contain' ? 'object-contain' : 'object-cover');
    return (
        <div
            ref={boxRef}
            className={cn('absolute inset-0 touch-none cursor-ew-resize select-none overflow-hidden', className)}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
        >
            <img src={after} alt="" draggable="false" className={imgCls} style={{ objectPosition: position, ...afterStyle }} />
            <img src={before} alt="" draggable="false" className={imgCls} style={{ objectPosition: position, clipPath: `inset(0 ${100 - pos}% 0 0)` }} />

            {beforeLabel && <span className="pointer-events-none absolute bottom-2 left-2 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-semibold text-white">{beforeLabel}</span>}
            {afterLabel && <span className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-semibold text-white">{afterLabel}</span>}

            {/* divider + handle */}
            <div className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_4px_rgba(0,0,0,0.5)]" style={{ left: `${pos}%` }} />
            <button
                type="button"
                role="slider"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(pos)}
                aria-label={`${beforeLabel || ''} / ${afterLabel || ''}`}
                onKeyDown={onKeyDown}
                className="absolute top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-zinc-700 shadow-lg ring-2 ring-primary focus:outline-none focus-visible:ring-4"
                style={{ left: `${pos}%` }}
            >
                <IconChevronLeft size={14} strokeWidth={2.5} className="-mr-1" />
                <IconChevronRight size={14} strokeWidth={2.5} className="-ml-1" />
            </button>
        </div>
    );
}
